import { getTurns } from "./conversations";
import type { TurnRow } from "./types";

/**
 * Client-side cache for conversation turns, with in-flight de-duplication.
 *
 * Opening a conversation used to re-fetch its full turn history over the
 * network every single time — even when switching back to one viewed seconds
 * ago — and showed a "Loading history…" placeholder for the whole round-trip.
 * This cache lets callers render a previously-loaded transcript instantly
 * (stale-while-revalidate) and lets the sidebar prefetch on hover so the data
 * is already present by the time a row is clicked.
 *
 * Scope: module-level (per browser tab). Bounded by a small LRU so a long
 * session cannot grow it without limit.
 */

const MAX_ENTRIES = 40;

function key(project: string, conversationId: number): string {
  return `${project}:${conversationId}`;
}

// Insertion-ordered Map doubles as an LRU: re-inserting moves a key to the end,
// and we evict from the front (oldest) when over capacity.
const cache = new Map<string, TurnRow[]>();
const inFlight = new Map<string, Promise<TurnRow[]>>();

function touch(k: string, turns: TurnRow[]): void {
  cache.delete(k);
  cache.set(k, turns);
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/** Synchronously read cached turns, or undefined on a miss. */
export function getCachedTurns(project: string, conversationId: number): TurnRow[] | undefined {
  return cache.get(key(project, conversationId));
}

/** Overwrite the cache entry for a conversation (e.g. after a fresh fetch). */
export function setCachedTurns(project: string, conversationId: number, turns: TurnRow[]): void {
  touch(key(project, conversationId), turns);
}

/**
 * Drop a conversation's cache entry so its next open revalidates from the
 * server. Called after a chat turn is sent, since the transcript changed.
 * A null/undefined id is a no-op (covers the "no active conversation" case).
 */
export function invalidateTurns(project: string, conversationId: number | null | undefined): void {
  if (conversationId == null) return;
  cache.delete(key(project, conversationId));
}

/**
 * Fetch turns through the cache with in-flight de-duplication: a hover-prefetch
 * already running for the same conversation is reused by the click that follows.
 * Resolves to the freshest fetched turns and updates the cache. The getTurns
 * call is wrapped in Promise.resolve so a synchronous throw becomes a rejection
 * the caller can handle (and prefetch can swallow).
 */
export function fetchTurns(project: string, conversationId: number): Promise<TurnRow[]> {
  const k = key(project, conversationId);
  const existing = inFlight.get(k);
  if (existing) return existing;
  const p = Promise.resolve()
    .then(() => getTurns(project, conversationId))
    .then((turns) => {
      touch(k, turns);
      return turns;
    })
    .finally(() => {
      inFlight.delete(k);
    });
  inFlight.set(k, p);
  return p;
}

/**
 * Best-effort warm of the cache (sidebar hover / pointer-down / focus). Skips
 * work when the entry is already cached or a fetch is in flight, and swallows
 * errors so a failed prefetch never surfaces to the user — the real open will
 * retry and report properly.
 */
export function prefetchTurns(project: string, conversationId: number): void {
  const k = key(project, conversationId);
  if (cache.has(k) || inFlight.has(k)) return;
  void fetchTurns(project, conversationId).catch(() => { /* best-effort */ });
}

/** Test-only: clear all cache + in-flight state so tests stay isolated. */
export function __clearTurnsCache(): void {
  cache.clear();
  inFlight.clear();
}
